/**
 * UnitPreferenceContext — Wave B · Turn 1 real-user-gap closure.
 *
 * Lets a field user pick the display/measurement unit system (feet,
 * US survey feet, or meters) and persists it via
 * GET/PUT /api/users/me/preferences/measurement_units. localStorage is
 * used as an instant-load + offline fallback so the picked unit survives
 * a reload even before the network round trip resolves.
 */
import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { areaUnitForLengthUnit } from "@/lib/units";
import { useAuth } from "@/contexts/AuthContext";

const STORAGE_KEY = "fop_measurement_units";
const DEFAULT_UNIT = "ft";

const UnitPreferenceContext = createContext(null);

export function UnitPreferenceProvider({ children }) {
  const { user } = useAuth();
  const [lengthUnit, setLengthUnitState] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) || DEFAULT_UNIT; } catch { return DEFAULT_UNIT; }
  });

  useEffect(() => {
    // MEDIUM fix (real-plan cert iteration_183) — this fired on every
    // cold load before AuthContext's checkAuth() resolved, hitting an
    // authenticated endpoint with no bearer applied yet and 401'ing on
    // every reload (silently — the server sync was dead, only the
    // localStorage bootstrap value ever worked). Wait for `user` to
    // resolve to an authenticated object first.
    if (!user) return;
    let cancel = false;
    api.get("/users/me/preferences").then(({ data }) => {
      if (!cancel && data?.measurement_units) {
        setLengthUnitState(data.measurement_units);
        try { localStorage.setItem(STORAGE_KEY, data.measurement_units); } catch { /* ignore */ }
      }
    }).catch(() => { /* offline / not-yet-set — keep local default */ });
    return () => { cancel = true; };
  }, [user]);

  const setLengthUnit = useCallback((unit) => {
    setLengthUnitState(unit);
    try { localStorage.setItem(STORAGE_KEY, unit); } catch { /* ignore */ }
    api.put("/users/me/preferences/measurement_units", { value: unit }).catch(() => { /* offline — local value still wins on this device */ });
  }, []);

  const value = {
    lengthUnit,
    areaUnit: areaUnitForLengthUnit(lengthUnit),
    setLengthUnit,
  };

  return <UnitPreferenceContext.Provider value={value}>{children}</UnitPreferenceContext.Provider>;
}

export function useUnitPreference() {
  const ctx = useContext(UnitPreferenceContext);
  if (!ctx) throw new Error("useUnitPreference must be used within UnitPreferenceProvider");
  return ctx;
}
